import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import useNoIndex from "@/lib/useNoIndex";
import {
  HeartHandshake, Clock, MapPin, Download, AlertCircle, Loader2,
  CheckCircle2, XCircle, Sparkles, FileText, ShieldCheck, RotateCw, Copy,
} from "lucide-react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import BridalQuoteStudioView from "@/components/BridalQuoteStudioView";

// ---------- i18n dictionary ----------
// Keys mirror the full UI so the public signing page reads in the client's language
// (it | en | es | fr). Fallback = it.
const LABELS = {
  it: {
    loading_error: "Link non valido",
    loading_error_desc: "Il link condiviso non è valido o è stato revocato.",
    hero_caption: "Documenti per il tuo matrimonio",
    intro_kicker: "La Sua proposta personalizzata",
    intro_title: "Il Suo matrimonio merita qualcosa di davvero speciale",
    intro_subtitle: "Una proposta creata su misura per accompagnarla nel suo giorno più importante.",
    intro_paragraph: "Abbiamo pensato a questa proposta per valorizzare al meglio il Suo stile e rendere il Suo matrimonio armonioso e curato in ogni dettaglio.",
    ceremony: "Cerimonia",
    by_studio: "a cura di",
    download_pdf: "Scarica PDF preventivo + contratto",
    accepted_title: "Preventivo accettato e contratto firmato",
    accepted_on: "Firmato il",
    accepted_thanks: "grazie per la fiducia!",
    accepted_signed_fallback: "Firmato",
    signature_email: "Email firma:",
    document_id: "ID documento:",
    rejected_title: "Preventivo rifiutato",
    rejected_on: "Risposta del",
    rejected_fallback: "Risposta registrata",
    rejected_reason: "Motivo:",
    quote_kicker: "Preventivo",
    quote_title: "Riepilogo voci",
    contract_kicker: "Contratto",
    contract_title: "Condizioni e termini",
    no_items: "Nessuna voce nel preventivo.",
    th_description: "Descrizione",
    th_qty: "Q.tà",
    th_price: "Prezzo",
    th_total: "Totale",
    discount_applied: "Sconto {p}% applicato",
    subtotal: "Subtotale",
    general_discount: "Sconto",
    vat: "IVA",
    total_row: "Totale",
    deposit: "Acconto",
    balance: "Saldo",
    conditions_section: "Condizioni generali",
    payment_section: "Termini di pagamento",
    acceptance_note:
      'Il presente documento costituisce accordo tra le parti e viene accettato integralmente con la firma digitale (click su "Accetto").',
    esign_title: "Firma elettronica",
    esign_desc:
      "Confermando, il documento verrà firmato digitalmente con verifica via email (codice OTP). Lo studio verrà avvisato in tempo reale.",
    esign_consent:
      "Dichiaro di aver letto e accettato integralmente il preventivo e le condizioni contrattuali.",
    accept_sign: "Accetta e firma",
    reject: "Rifiuto",
    otp_sent_title: "Abbiamo inviato un codice di verifica alla tua email",
    otp_sent_to: "a",
    otp_expiry_note: "Il codice scade in 10 minuti. Massimo 5 tentativi.",
    demo_label: "DEMO",
    demo_note: "Modalità demo · email non realmente inviata. Codice:",
    copy: "Copia",
    otp_input_label: "Codice di verifica",
    otp_error_6digits: "Inserisci un codice di 6 cifre",
    confirm_signature: "Conferma firma",
    resend_code: "Invia nuovo codice",
    cancel: "Annulla",
    powered_by: "Powered by",
    reject_dialog_title: "Confermi il rifiuto?",
    reject_dialog_desc: "Puoi lasciare un messaggio per lo studio. È del tutto facoltativo.",
    reject_reason_label: "Motivo (opzionale)",
    reject_placeholder: "Es. Cambio fornitore · budget non disponibile · …",
    confirm_reject: "Conferma rifiuto",
    toast_code_sent: "Codice inviato",
    toast_signature_complete: "Firma elettronica completata",
    toast_copied: "Codice copiato",
    toast_reply_sent: "Risposta inviata",
    toast_network_error: "Errore di rete",
    toast_invalid_code: "Codice non valido",
    err_send_code: "Errore invio codice",
    included_services_section: "Servizi inclusi",
    optional_services_section: "Servizi opzionali",
    optional_subtitle: "Se desidera aggiungere servizi extra al preventivo, selezioni le opzioni qui sotto.",
    optional_instructions: "Attivi il flag sui servizi che desidera aggiungere al preventivo.",
    optional_window_note: "Le modifiche saranno disponibili fino all'accettazione o al rifiuto del preventivo.",
    optional_added: "Scelto per il Suo matrimonio",
    optional_not_added: "Non selezionato",
    optional_pending: "Opzionale",
    optional_just_added: "✔ Aggiunto alla Sua proposta",
    optional_locked: "Le selezioni opzionali sono bloccate dopo la firma o il rifiuto del preventivo.",
    optional_quantity: "Q.tà",
    toast_options_updated: "Selezioni aggiornate",
    toast_options_error: "Impossibile aggiornare le selezioni",
    notes_section_title: "Note del preventivo",
    notes_section_subtitle: "Condizioni e indicazioni che fanno parte integrante del preventivo.",
    confirm_quote_title: "Conferma la proposta",
    confirm_quote_desc: "Approva la proposta. Il contratto verrà firmato in un momento successivo.",
    confirm_quote_button: "Conferma la proposta",
    confirm_quote_subtext:
      "Una volta confermata, la proposta resterà riservata per 24 ore. Potrà procedere con la firma del contratto in un secondo momento.",
    toast_quote_confirmed: "Proposta confermata. Riceverà il contratto a breve.",
    quote_status: { bozza: "Bozza", inviato: "In attesa di risposta", accettato: "Accettato", rifiutato: "Rifiutato" },
    locale: "it-IT",
  },
  en: {
    loading_error: "Invalid link",
    loading_error_desc: "The shared link is no longer valid or has been revoked.",
    hero_caption: "Documents for your wedding",
    intro_kicker: "Your bespoke proposal",
    intro_title: "Your wedding deserves something truly special",
    intro_subtitle: "A bespoke proposal created to accompany you on your most important day.",
    intro_paragraph: "We have crafted this proposal to enhance your style and make your wedding harmonious and beautifully curated in every detail.",
    ceremony: "Ceremony",
    by_studio: "by",
    download_pdf: "Download quote + contract PDF",
    accepted_title: "Quote accepted and contract signed",
    accepted_on: "Signed on",
    accepted_thanks: "thank you for your trust!",
    accepted_signed_fallback: "Signed",
    signature_email: "Signature email:",
    document_id: "Document ID:",
    rejected_title: "Quote rejected",
    rejected_on: "Reply on",
    rejected_fallback: "Reply recorded",
    rejected_reason: "Reason:",
    quote_kicker: "Quote",
    quote_title: "Quote summary",
    contract_kicker: "Contract",
    contract_title: "Terms and conditions",
    no_items: "No items in the quote.",
    th_description: "Description",
    th_qty: "Qty",
    th_price: "Price",
    th_total: "Total",
    discount_applied: "{p}% discount applied",
    subtotal: "Subtotal",
    general_discount: "Discount",
    vat: "VAT",
    total_row: "Total",
    deposit: "Deposit",
    balance: "Balance",
    conditions_section: "General conditions",
    payment_section: "Payment terms",
    acceptance_note:
      'This document constitutes the agreement between the parties and is fully accepted upon digital signature (click on "Accept").',
    esign_title: "Electronic signature",
    esign_desc:
      "By confirming, the document will be digitally signed with email verification (OTP code). The studio will be notified in real time.",
    esign_consent:
      "I declare that I have read and fully accepted the quote and the contractual conditions.",
    accept_sign: "Accept and sign",
    reject: "Reject",
    otp_sent_title: "We sent a verification code to your email",
    otp_sent_to: "to",
    otp_expiry_note: "The code expires in 10 minutes. Up to 5 attempts.",
    demo_label: "DEMO",
    demo_note: "Demo mode · email not actually sent. Code:",
    copy: "Copy",
    otp_input_label: "Verification code",
    otp_error_6digits: "Enter a 6-digit code",
    confirm_signature: "Confirm signature",
    resend_code: "Send a new code",
    cancel: "Cancel",
    powered_by: "Powered by",
    reject_dialog_title: "Confirm rejection?",
    reject_dialog_desc: "You can leave a message for the studio. It is entirely optional.",
    reject_reason_label: "Reason (optional)",
    reject_placeholder: "E.g. Changed supplier · budget not available · …",
    confirm_reject: "Confirm rejection",
    toast_code_sent: "Code sent",
    toast_signature_complete: "Electronic signature completed",
    toast_copied: "Code copied",
    toast_reply_sent: "Reply sent",
    toast_network_error: "Network error",
    toast_invalid_code: "Invalid code",
    err_send_code: "Error sending code",
    included_services_section: "Included services",
    optional_services_section: "Optional services",
    optional_subtitle: "If you'd like to add extra services to the quote, tick the options below.",
    optional_instructions: "Tick the services you'd like to add to your quote.",
    optional_window_note: "Changes remain available until the quote is accepted or declined.",
    optional_added: "Chosen for your wedding",
    optional_not_added: "Not selected",
    optional_pending: "Optional",
    optional_just_added: "✔ Added to your proposal",
    optional_locked: "Optional selections are locked after the quote is signed or rejected.",
    optional_quantity: "Qty",
    toast_options_updated: "Selections updated",
    toast_options_error: "Unable to update your selections",
    notes_section_title: "Quote notes",
    notes_section_subtitle: "Conditions and details that are part of this quote.",
    confirm_quote_title: "Confirm the proposal",
    confirm_quote_desc: "Approve the proposal. The contract will be signed in a separate step.",
    confirm_quote_button: "Confirm the proposal",
    confirm_quote_subtext:
      "Once confirmed, this proposal will be reserved for 24 hours. You will be able to proceed with the contract signing afterwards.",
    toast_quote_confirmed: "Proposal confirmed. You will receive the contract shortly.",
    quote_status: { bozza: "Draft", inviato: "Pending reply", accettato: "Accepted", rifiutato: "Rejected" },
    locale: "en-GB",
  },
  es: {
    loading_error: "Enlace no válido",
    loading_error_desc: "El enlace compartido ya no es válido o ha sido revocado.",
    hero_caption: "Documentos para tu boda",
    intro_kicker: "Su propuesta personalizada",
    intro_title: "Su boda merece algo verdaderamente especial",
    intro_subtitle: "Una propuesta creada a medida para acompañarle en su día más importante.",
    intro_paragraph: "Hemos pensado esta propuesta para realzar su estilo y hacer que su boda sea armoniosa y cuidada en cada detalle.",
    ceremony: "Ceremonia",
    by_studio: "a cargo de",
    download_pdf: "Descargar PDF presupuesto + contrato",
    accepted_title: "Presupuesto aceptado y contrato firmado",
    accepted_on: "Firmado el",
    accepted_thanks: "¡gracias por tu confianza!",
    accepted_signed_fallback: "Firmado",
    signature_email: "Email firma:",
    document_id: "ID documento:",
    rejected_title: "Presupuesto rechazado",
    rejected_on: "Respuesta del",
    rejected_fallback: "Respuesta registrada",
    rejected_reason: "Motivo:",
    quote_kicker: "Presupuesto",
    quote_title: "Resumen",
    contract_kicker: "Contrato",
    contract_title: "Condiciones",
    no_items: "No hay elementos en el presupuesto.",
    th_description: "Descripción",
    th_qty: "Cant.",
    th_price: "Precio",
    th_total: "Total",
    discount_applied: "Descuento del {p}% aplicado",
    subtotal: "Subtotal",
    general_discount: "Descuento",
    vat: "IVA",
    total_row: "Total",
    deposit: "Anticipo",
    balance: "Saldo",
    conditions_section: "Condiciones generales",
    payment_section: "Condiciones de pago",
    acceptance_note:
      'Este documento constituye el acuerdo entre las partes y se acepta íntegramente con la firma digital (clic en "Aceptar").',
    esign_title: "Firma electrónica",
    esign_desc:
      "Al confirmar, el documento se firmará digitalmente con verificación por email (código OTP). El estudio será notificado en tiempo real.",
    esign_consent:
      "Declaro haber leído y aceptado íntegramente el presupuesto y las condiciones contractuales.",
    accept_sign: "Aceptar y firmar",
    reject: "Rechazar",
    otp_sent_title: "Hemos enviado un código de verificación a tu correo",
    otp_sent_to: "a",
    otp_expiry_note: "El código caduca en 10 minutos. Máximo 5 intentos.",
    demo_label: "DEMO",
    demo_note: "Modo demo · correo no enviado realmente. Código:",
    copy: "Copiar",
    otp_input_label: "Código de verificación",
    otp_error_6digits: "Introduce un código de 6 cifras",
    confirm_signature: "Confirmar firma",
    resend_code: "Enviar nuevo código",
    cancel: "Cancelar",
    powered_by: "Powered by",
    reject_dialog_title: "¿Confirmar el rechazo?",
    reject_dialog_desc: "Puedes dejar un mensaje para el estudio. Es totalmente opcional.",
    reject_reason_label: "Motivo (opcional)",
    reject_placeholder: "Ej. Cambio de proveedor · presupuesto no disponible · …",
    confirm_reject: "Confirmar rechazo",
    toast_code_sent: "Código enviado",
    toast_signature_complete: "Firma electrónica completada",
    toast_copied: "Código copiado",
    toast_reply_sent: "Respuesta enviada",
    toast_network_error: "Error de red",
    toast_invalid_code: "Código no válido",
    err_send_code: "Error al enviar el código",
    included_services_section: "Servicios incluidos",
    optional_services_section: "Servicios opcionales",
    optional_subtitle: "Si desea añadir servicios extra al presupuesto, marque las opciones a continuación.",
    optional_instructions: "Marque los servicios que desea añadir al presupuesto.",
    optional_window_note: "Los cambios estarán disponibles hasta la aceptación o el rechazo del presupuesto.",
    optional_added: "Confirmado por el cliente",
    optional_not_added: "No seleccionado",
    optional_pending: "Opcional",
    optional_locked: "Las selecciones opcionales se bloquean tras firmar o rechazar el presupuesto.",
    optional_quantity: "Cant.",
    toast_options_updated: "Selecciones actualizadas",
    toast_options_error: "No se pueden actualizar las selecciones",
    notes_section_title: "Notas del presupuesto",
    notes_section_subtitle: "Condiciones e indicaciones que forman parte del presupuesto.",
    confirm_quote_title: "Confirmar el presupuesto",
    confirm_quote_desc: "Aprueba el presupuesto. El contrato se firmará en un momento posterior.",
    confirm_quote_button: "Confirmar presupuesto",
    toast_quote_confirmed: "Presupuesto confirmado. Recibirás el contrato en breve.",
    quote_status: { bozza: "Borrador", inviato: "Pendiente", accettato: "Aceptado", rifiutato: "Rechazado" },
    locale: "es-ES",
  },
  fr: {
    loading_error: "Lien non valide",
    loading_error_desc: "Le lien partagé n'est plus valide ou a été révoqué.",
    hero_caption: "Documents pour votre mariage",
    intro_kicker: "Votre proposition sur mesure",
    intro_title: "Votre mariage mérite quelque chose de vraiment spécial",
    intro_subtitle: "Une proposition créée sur mesure pour vous accompagner dans votre jour le plus important.",
    intro_paragraph: "Nous avons conçu cette proposition pour mettre en valeur votre style et rendre votre mariage harmonieux et soigné dans chaque détail.",
    ceremony: "Cérémonie",
    by_studio: "par",
    download_pdf: "Télécharger PDF devis + contrat",
    accepted_title: "Devis accepté et contrat signé",
    accepted_on: "Signé le",
    accepted_thanks: "merci pour votre confiance !",
    accepted_signed_fallback: "Signé",
    signature_email: "E-mail signature :",
    document_id: "ID du document :",
    rejected_title: "Devis refusé",
    rejected_on: "Réponse du",
    rejected_fallback: "Réponse enregistrée",
    rejected_reason: "Motif :",
    quote_kicker: "Devis",
    quote_title: "Récapitulatif",
    contract_kicker: "Contrat",
    contract_title: "Conditions",
    no_items: "Aucun élément dans le devis.",
    th_description: "Description",
    th_qty: "Qté",
    th_price: "Prix",
    th_total: "Total",
    discount_applied: "Remise de {p}% appliquée",
    subtotal: "Sous-total",
    general_discount: "Remise",
    vat: "TVA",
    total_row: "Total",
    deposit: "Acompte",
    balance: "Solde",
    conditions_section: "Conditions générales",
    payment_section: "Conditions de paiement",
    acceptance_note:
      'Ce document constitue l\'accord entre les parties et est intégralement accepté par signature numérique (clic sur "Accepter").',
    esign_title: "Signature électronique",
    esign_desc:
      "En confirmant, le document sera signé numériquement avec vérification par e-mail (code OTP). Le studio sera notifié en temps réel.",
    esign_consent:
      "Je déclare avoir lu et intégralement accepté le devis et les conditions contractuelles.",
    accept_sign: "Accepter et signer",
    reject: "Refuser",
    otp_sent_title: "Nous avons envoyé un code de vérification à votre e-mail",
    otp_sent_to: "à",
    otp_expiry_note: "Le code expire dans 10 minutes. Jusqu'à 5 tentatives.",
    demo_label: "DEMO",
    demo_note: "Mode démo · e-mail non réellement envoyé. Code :",
    copy: "Copier",
    otp_input_label: "Code de vérification",
    otp_error_6digits: "Saisissez un code à 6 chiffres",
    confirm_signature: "Confirmer la signature",
    resend_code: "Envoyer un nouveau code",
    cancel: "Annuler",
    powered_by: "Powered by",
    reject_dialog_title: "Confirmer le refus ?",
    reject_dialog_desc: "Vous pouvez laisser un message pour le studio. C'est entièrement facultatif.",
    reject_reason_label: "Motif (facultatif)",
    reject_placeholder: "Ex. Changement de prestataire · budget indisponible · …",
    confirm_reject: "Confirmer le refus",
    toast_code_sent: "Code envoyé",
    toast_signature_complete: "Signature électronique terminée",
    toast_copied: "Code copié",
    toast_reply_sent: "Réponse envoyée",
    toast_network_error: "Erreur réseau",
    toast_invalid_code: "Code non valide",
    err_send_code: "Erreur d'envoi du code",
    included_services_section: "Prestations incluses",
    optional_services_section: "Prestations optionnelles",
    optional_subtitle: "Si vous souhaitez ajouter des prestations au devis, cochez les options ci-dessous.",
    optional_instructions: "Cochez les prestations que vous souhaitez ajouter au devis.",
    optional_window_note: "Les modifications restent possibles jusqu'à l'acceptation ou au refus du devis.",
    optional_added: "Confirmé par le client",
    optional_not_added: "Non sélectionné",
    optional_pending: "En option",
    optional_locked: "Les sélections optionnelles sont verrouillées après signature ou refus du devis.",
    optional_quantity: "Qté",
    toast_options_updated: "Sélections mises à jour",
    toast_options_error: "Impossible de mettre à jour les sélections",
    notes_section_title: "Notes du devis",
    notes_section_subtitle: "Conditions et indications faisant partie intégrante du devis.",
    confirm_quote_title: "Confirmer le devis",
    confirm_quote_desc: "Approuvez le devis. Le contrat sera signé ultérieurement.",
    confirm_quote_button: "Confirmer le devis",
    toast_quote_confirmed: "Devis confirmé. Vous recevrez le contrat sous peu.",
    quote_status: { bozza: "Brouillon", inviato: "En attente", accettato: "Accepté", rifiutato: "Refusé" },
    locale: "fr-FR",
  },
};

export default function PublicClient() {
  useNoIndex();
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  // Electronic signature flow state
  const [consent, setConsent] = useState(false);
  const [signStep, setSignStep] = useState("idle"); // idle | otp_sent | signing
  const [otpCode, setOtpCode] = useState("");
  const [otpSentTo, setOtpSentTo] = useState("");
  const [otpExpiresAt, setOtpExpiresAt] = useState("");
  const [demoOtp, setDemoOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  // Local copy of quote.items so the client can toggle optionals + change qty
  // with instant UI feedback (optimistic). Server is the source of truth, so we
  // mirror back whatever the API returns after each successful save.
  const [items, setItems] = useState([]);
  const [optBusy, setOptBusy] = useState(false);
  // Index of the optional service the user just toggled ON — drives the
  // soft "Aggiunto alla Sua proposta" feedback. Reset to null after ~2.4s.
  const [justAddedOpt, setJustAddedOpt] = useState(null);
  // Language override coming from URL (?lang=…). Empty string = no override,
  // server falls back to client.language. Re-fetches whenever this changes.
  const langParam = (searchParams.get("lang") || "").toLowerCase();
  const SUPPORTED_LANGS = ["it", "en", "es", "fr"];
  const langOverride = SUPPORTED_LANGS.includes(langParam) ? langParam : "";
  // Tracks the currently-shown language for the switcher highlight. Falls
  // back to whatever the server resolved (URL → client.language → "it").
  const language = data?.client?.language || langOverride || "it";
  const t = useMemo(() => LABELS[language] || LABELS.it, [language]);

  const refresh = async () => {
    setLoading(true);
    try {
      const url = langOverride
        ? `${API}/public/client/${token}?lang=${langOverride}`
        : `${API}/public/client/${token}`;
      const r = await fetch(url);
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.detail || "Link non valido");
      }
      const json = await r.json();
      setData(json);
      setItems(Array.isArray(json?.quote?.items) ? json.quote.items : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Refetch whenever the token or the URL-driven lang override changes.
  // This is what powers the language switcher: a click writes ?lang=XX
  // into the URL → React-router updates searchParams → this hook fires →
  // we hit the cached translation and re-render in the new language.
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [token, langOverride]);

  /**
   * Switch the public-page language. We write the choice to the URL so
   * a refresh / shared link preserves it; the actual data reload is
   * driven by the `langOverride`-dependent useEffect above.
   */
  const switchLanguage = (next) => {
    if (!SUPPORTED_LANGS.includes(next)) return;
    const params = new URLSearchParams(searchParams);
    if (next === (data?.client?.language || "it") && !langOverride) {
      // Switching back to the client's default language → strip the query
      // param entirely for a clean URL.
      params.delete("lang");
    } else {
      params.set("lang", next);
    }
    setSearchParams(params, { replace: true });
  };

  // Push the current optional-items selection to the backend.
  // Sends ALL optional items so the server has a single, consistent snapshot.
  // The server validates each row (only is_optional=true rows are touchable),
  // recomputes totals, and replies with the canonical quote payload.
  const pushOptionalChange = async (nextItems) => {
    const payload = {
      items: nextItems
        .map((it, idx) => ({ it, idx }))
        .filter(({ it }) => !!it.is_optional)
        .map(({ it, idx }) => ({
          index: idx,
          selected: !!it.client_selected,
          qty: parseFloat(it.qty) || 0,
        })),
    };
    if (!payload.items.length) return;
    setOptBusy(true);
    try {
      const r = await fetch(`${API}/public/client/${token}/quote-options`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.detail || t.toast_options_error);
      }
      const j = await r.json();
      // Sync canonical totals + items from the server response.
      if (j?.quote) {
        setData((d) => (d ? { ...d, quote: { ...d.quote, ...j.quote } } : d));
        if (Array.isArray(j.quote.items)) setItems(j.quote.items);
      }
      toast.success(t.toast_options_updated);
    } catch (e) {
      toast.error(e.message || t.toast_options_error);
      // Revert optimistic local change by re-syncing from current `data`.
      setItems(Array.isArray(data?.quote?.items) ? data.quote.items : []);
    } finally {
      setOptBusy(false);
    }
  };

  const toggleOptionalSelected = (idx, value) => {
    // 3-state mapping: checkbox true → "accepted", checkbox false → "declined"
    // (explicit rejection kept for history — distinct from "pending" which
    // means the client hasn't acted yet).
    const decision = value ? "accepted" : "declined";
    // Surface the soft "Aggiunto alla Sua proposta" feedback for ~2.4s when
    // the user is *adding* (not removing) an extra. Auto-fades.
    if (value) {
      setJustAddedOpt(idx);
      window.setTimeout(() => {
        setJustAddedOpt((cur) => (cur === idx ? null : cur));
      }, 2400);
    }
    const next = items.map((it, i) =>
      i === idx
        ? { ...it, client_decision: decision, client_selected: !!value }
        : it,
    );
    setItems(next);
    pushOptionalChange(next);
  };

  const updateOptionalQty = (idx, raw) => {
    let qty = parseFloat(raw);
    if (isNaN(qty) || qty < 0) qty = 0;
    if (qty > 1000) qty = 1000;
    const next = items.map((it, i) => (i === idx ? { ...it, qty } : it));
    setItems(next);
    // We debounce qty saves: persist on blur (handled inline), not on every keystroke.
    return next;
  };

  const requestOtp = async () => {
    if (!consent) return;
    setSubmitting(true);
    setOtpError("");
    try {
      const r = await fetch(`${API}/public/client/${token}/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || t.err_send_code);
      setOtpSentTo(d.sent_to || data?.client?.signature_email || "");
      setOtpExpiresAt(d.expires_at || "");
      setDemoOtp(d.demo_mode && d.demo_otp ? d.demo_otp : "");
      setSignStep("otp_sent");
      toast.success(t.toast_code_sent);
    } catch (e) {
      toast.error(e.message);
    } finally { setSubmitting(false); }
  };

  const verifyOtp = async () => {
    const code = (otpCode || "").trim();
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      setOtpError(t.otp_error_6digits);
      return;
    }
    setSubmitting(true);
    setOtpError("");
    try {
      const r = await fetch(`${API}/public/client/${token}/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      const d = await r.json();
      if (!r.ok) {
        setOtpError(d?.detail || t.toast_invalid_code);
        toast.error(d?.detail || t.toast_invalid_code);
        return;
      }
      toast.success(t.toast_signature_complete);
      setSignStep("idle");
      setOtpCode("");
      setDemoOtp("");
      await refresh();
    } catch (e) {
      toast.error(e.message);
    } finally { setSubmitting(false); }
  };

  const resendOtp = async () => {
    setOtpCode("");
    setOtpError("");
    await requestOtp();
  };

  const copyDemoOtp = async () => {
    try {
      await navigator.clipboard.writeText(demoOtp);
      toast.success(t.toast_copied);
    } catch {/* ignore */}
  };

  // Confirm the quote without signing the contract — used when the operator has
  // not (yet) shared the contract with the client. The signing step is skipped.
  const confirmQuoteOnly = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/public/client/${token}/accept-quote-only`, {
        method: "POST",
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d?.detail || t.err_generic);
        return;
      }
      toast.success(t.toast_quote_confirmed);
      await refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const reject = async () => {
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/public/client/${token}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: rejectReason || "" }),
      });
      if (!r.ok) throw new Error(t.toast_network_error);
      toast.success(t.toast_reply_sent);
      setRejectOpen(false);
      setRejectReason("");
      await refresh();
    } catch (e) {
      toast.error(e.message);
    } finally { setSubmitting(false); }
  };

  if (loading && !data) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#F8F5F1" }}
      >
        <Loader2 className="w-8 h-8 text-primary animate-spin" strokeWidth={1.5} />
      </div>
    );
  }

  if (error) {
    // Uses Italian-default strings for the error boundary since we may not have loaded data yet.
    const defT = LABELS.it;
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        style={{ backgroundColor: "#F8F5F1" }}
      >
        <div className="bg-white border border-border rounded-sm p-10 max-w-md text-center" data-testid="public-client-error">
          <AlertCircle className="w-10 h-10 text-neutral-400 mx-auto mb-4" strokeWidth={1.5} />
          <h1 className="font-serif-heading text-3xl">{defT.loading_error}</h1>
          <p className="text-sm text-neutral-600 mt-2">{error || defT.loading_error_desc}</p>
        </div>
      </div>
    );
  }

  const couple = `${data.client.name}${data.client.partner_name ? " & " + data.client.partner_name : ""}`;
  const quote = data.quote;
  const contract = data.contract;
  // The contract is presented to the client ONLY when the operator has actually
  // sent it (contract.status in {inviato, firmato}). When false, the page shows
  // a quote-only confirmation flow without OTP / signature.
  const contractVisible = !!data.contract_visible;
  const quoteAccepted = quote.status === "accettato";
  const rejected = quote.status === "rifiutato";
  const contractSigned = contract.status === "firmato";
  // Two distinct UI gates:
  //  • optionalsLocked  → freeze the optional checkboxes once the client has
  //    accepted (with or without contract) or rejected. Prevents tampering
  //    after a binding decision.
  //  • signFlowFinished → the signing CTA disappears only when the journey is
  //    truly over: contract signed, OR quote rejected, OR (quote accepted AND
  //    contract was never shared = quote-only flow done).
  const decisionTaken = quoteAccepted || rejected;
  const signFlowFinished = rejected || contractSigned || (quoteAccepted && !contractVisible);
  // Banner logic:
  //  • "fully signed" banner → contract.status === "firmato"
  //  • "quote-only accepted" banner → quoteAccepted && !contractVisible (operator
  //    chose to send only the quote and the client confirmed without OTP)
  //  • "rejected" banner → rejected
  // Critical: when quoteAccepted but contractVisible && !contractSigned, we
  // are in the second-step phase — operator sent the contract AFTER the client
  // had already confirmed the quote-only. The signing CTA MUST still render.
  const showAcceptedFullBanner = contractSigned;
  const showQuoteOnlyAcceptedBanner = quoteAccepted && !contractVisible;
  const acceptedAt = contractSigned
    ? (contract.signed_at ? new Date(contract.signed_at) : (quote.accepted_at ? new Date(quote.accepted_at) : null))
    : (quote.accepted_at ? new Date(quote.accepted_at) : null);
  const rejectedAt = quote.rejected_at ? new Date(quote.rejected_at) : null;
  const fmt = (n) => `€ ${(Number(n) || 0).toFixed(2)}`;

  // Resolve the 3-state decision with legacy fallback. Non-optional items
  // are always "accepted" conceptually (they're always in the total).
  const decisionOf = (it) => {
    if (!it.is_optional) return "accepted";
    const d = (it.client_decision || "").toLowerCase();
    if (d === "accepted" || d === "declined" || d === "pending") return d;
    // Legacy docs: client_selected true/false → accepted/pending (no explicit
    // decline signal existed before this feature).
    return it.client_selected ? "accepted" : "pending";
  };

  // Split items + live totals (recomputed locally so toggles feel instant).
  const allItems = items.length ? items : (quote.items || []);
  const includedItems = allItems.filter((it) => decisionOf(it) === "accepted");
  const optionalItems = allItems
    .map((it, idx) => ({ it, idx }))
    .filter(({ it }) => !!it.is_optional);
  const liveSubtotal = includedItems.reduce((s, it) => {
    const qty = parseFloat(it.qty) || 0;
    const price = parseFloat(it.price) || 0;
    const disc = parseFloat(it.discount) || 0;
    return s + qty * price * (1 - disc / 100);
  }, 0);
  const liveAfterDisc = liveSubtotal * (1 - (parseFloat(quote.discount_total) || 0) / 100);
  const liveTax = parseFloat(quote.tax) || 0;
  const liveTotal = liveAfterDisc * (1 + liveTax / 100);
  const liveDeposit = Array.isArray(quote.deposits) && quote.deposits.length > 0
    ? quote.deposits.reduce((s, d) => s + (parseFloat(d?.amount) || 0), 0)
    : (parseFloat(quote.deposit) || 0);
  const liveBalance = Math.max(liveTotal - liveDeposit, 0);

  // Bride identifier — ALWAYS use the full name as entered by the operator.
  // Never split on whitespace: doing so risks whispering only a surname when
  // the operator entered "Cognome Nome" or a single-token name.
  const brideName = (data.client.name || "").trim();
  const heroBrandName = (data.owner_brand_name || data.owner_name || "").trim();

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: "#F8F5F1", color: "#4A4A4A" }}
      data-testid="public-client-page"
    >
      {/* Local CSS — soft, intentional entrance. Each block enters a moment
          after the previous one so the page feels like a slow reveal rather
          than a dump of information. */}
      <style>{`
        @keyframes pcFadeUp {
          0%   { opacity: 0; transform: translateY(14px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes pcFadeIn {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        .pc-fade-up { animation: pcFadeUp 900ms ease-out both; }
        .pc-fade-in { animation: pcFadeIn 900ms ease-out both; }
        .pc-delay-1 { animation-delay: 80ms; }
        .pc-delay-2 { animation-delay: 220ms; }
        .pc-delay-3 { animation-delay: 360ms; }
        .pc-delay-4 { animation-delay: 520ms; }
        .pc-delay-5 { animation-delay: 700ms; }
        .pc-delay-6 { animation-delay: 880ms; }
      `}</style>
      {/* Floating language switcher — fixed top-right, discreet pill. The
          bride can toggle the entire public page (brochure + standard layout
          + PDF download) between it/en/es/fr. Stays visible while scrolling
          on desktop; collapses below the emotional hero on mobile via the
          shadow/blur backdrop. */}
      <LanguageSwitcher
        current={language}
        onChange={switchLanguage}
        loading={loading}
      />
      {/* Bridal Quote Studio™ — premium template renderer (AI Manager only).
          When the operator has a published template, we render its 5 sections
          ABOVE the standard PublicClient interaction layer. The interactive
          parts (quote acceptance, optionals, sign, pay) stay in the existing
          card-based layout below so we don't fork two divergent UX
          implementations. */}
      {data.quote_template ? (
        <BridalQuoteStudioView
          tpl={data.quote_template}
          ownerName={data.owner_brand_name || data.owner_name}
          language={language}
        />
      ) : null}
      <div className="max-w-3xl mx-auto pt-20 sm:pt-28 pb-14 px-5">
        {/* Emotional Hero — slow, personal opener. Each line enters a moment
            after the previous one so the bride feels she's being welcomed, not
            shown a webpage. Generous white space; no card, no border. */}
        <header
          className="text-center mb-16 sm:mb-20 px-2"
          data-testid="public-client-emotional-hero"
        >
          {data.owner_logo_url && data.owner_logo_url.startsWith("data:image/") ? (
            <img
              src={data.owner_logo_url}
              alt={heroBrandName || "Brand"}
              className="mx-auto mb-10 sm:mb-12 max-h-16 sm:max-h-20 w-auto object-contain pc-fade-in pc-delay-1"
              data-testid="public-client-hero-logo"
            />
          ) : (
            heroBrandName && (
              <div
                className="font-serif-heading mb-10 sm:mb-12 pc-fade-in pc-delay-1"
                style={{
                  color: "#4A4A4A",
                  fontSize: "clamp(1.4rem, 2.6vw, 1.85rem)",
                  letterSpacing: "0.04em",
                }}
                data-testid="public-client-hero-brand"
              >
                {heroBrandName}
              </div>
            )
          )}
          {brideName && (
            <p
              className="pc-fade-in pc-delay-2"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontStyle: "italic",
                color: "#A88B5C",
                fontSize: "clamp(1.1rem, 1.8vw, 1.35rem)",
                letterSpacing: "0.02em",
                marginBottom: "1.6rem",
              }}
              data-testid="public-client-hero-whisper"
            >
              Per {brideName},
            </p>
          )}
          <span
            className="label-uppercase block pc-fade-in pc-delay-3"
            style={{ color: "#C8A97E", letterSpacing: "0.32em", fontSize: "11px" }}
            data-testid="public-client-hero-kicker"
          >
            {t.intro_kicker}
          </span>
          <h1
            className="font-serif-heading mt-7 leading-[1.12] pc-fade-up pc-delay-3"
            style={{
              color: "#2C2C2C",
              fontSize: "clamp(2.1rem, 4.6vw, 3.4rem)",
              fontStyle: "italic",
              fontWeight: 500,
              letterSpacing: "-0.01em",
              maxWidth: "22ch",
              margin: "1.75rem auto 0",
            }}
            data-testid="public-client-hero-title"
          >
            {t.intro_title}
          </h1>
          <p
            className="mx-auto mt-7 max-w-xl leading-relaxed pc-fade-up pc-delay-4"
            style={{ color: "#6E6E6E", fontSize: "clamp(0.95rem, 1.4vw, 1.05rem)" }}
            data-testid="public-client-hero-subtitle"
          >
            {t.intro_subtitle}
          </p>
          <div
            className="mx-auto mt-12 h-px w-12 pc-fade-in pc-delay-5"
            style={{ backgroundColor: "#C8A97E", opacity: 0.55 }}
            aria-hidden="true"
          />
          <p
            className="mx-auto mt-12 max-w-2xl leading-[1.75] italic pc-fade-up pc-delay-5"
            style={{
              color: "#7B6A45",
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: "clamp(1.05rem, 1.55vw, 1.25rem)",
              letterSpacing: "0.005em",
            }}
            data-testid="public-client-hero-paragraph"
          >
            {t.intro_paragraph}
          </p>
          {heroBrandName && (
            <p
              className="mt-10 pc-fade-in pc-delay-6"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontStyle: "italic",
                color: "#A88B5C",
                fontSize: "clamp(0.95rem, 1.3vw, 1.05rem)",
                letterSpacing: "0.05em",
              }}
              data-testid="public-client-hero-signoff"
            >
              — con cura, {heroBrandName}
            </p>
          )}
        </header>

        {/* Soft transition card — borderless, gentle shadow lifting from the
            warm background. Acts as a quiet pause between the emotional hero
            and the proposal details. */}
        <div
          className="rounded-md p-8 sm:p-12 pc-fade-up pc-delay-6"
          style={{
            backgroundColor: "#FFFDFA",
            boxShadow: "0 1px 2px rgba(74,74,74,0.04), 0 18px 50px -28px rgba(122,98,68,0.18)",
          }}
        >
          <span
            className="label-uppercase block"
            style={{ color: "#C8A97E", letterSpacing: "0.28em", fontSize: "10px" }}
          >
            {t.hero_caption}
          </span>
          <h2
            className="font-serif-heading mt-3 leading-[1.08]"
            style={{
              fontSize: "clamp(2rem, 4.4vw, 3.25rem)",
              color: "#2C2C2C",
              fontWeight: 500,
              letterSpacing: "-0.005em",
            }}
            data-testid="public-client-couple"
          >
            {couple}
          </h2>
          <div
            className="mt-5 h-px w-10"
            style={{ backgroundColor: "#C8A97E", opacity: 0.45 }}
            aria-hidden="true"
          />
          <div className="mt-5 text-[15px] flex flex-wrap gap-x-7 gap-y-2" style={{ color: "#6E6E6E" }}>
            {data.client.wedding_date_formatted && (
              <span className="flex items-center gap-2">
                <HeartHandshake className="w-4 h-4" strokeWidth={1.5} style={{ color: "#C8A97E" }} /> {data.client.wedding_date_formatted}
              </span>
            )}
            {data.client.ceremony_time && (
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" strokeWidth={1.5} style={{ color: "#C8A97E" }} /> {t.ceremony} {data.client.ceremony_time}
              </span>
            )}
            {data.client.venue && (
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4" strokeWidth={1.5} style={{ color: "#C8A97E" }} /> {data.client.venue}
              </span>
            )}
          </div>
          {data.owner_name && (
            <div
              className="mt-5"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontStyle: "italic",
                color: "#A88B5C",
                fontSize: "0.95rem",
              }}
            >
              {t.by_studio} {data.owner_name}
            </div>
          )}
        </div>

        {/* Decision banner if already taken */}
        {showAcceptedFullBanner && (
          <div
            className="mt-12 sm:mt-16 rounded-md p-7 sm:p-9 flex items-start gap-4"
            style={{ backgroundColor: "rgba(200,169,126,0.07)", border: "1px solid rgba(200,169,126,0.35)" }}
            data-testid="public-banner-accepted"
          >
            <CheckCircle2 className="w-5 h-5 mt-1 shrink-0" strokeWidth={1.5} style={{ color: "#7B6A45" }} />
            <div className="flex-1">
              <div
                className="font-serif-heading italic"
                style={{ fontSize: "1.5rem", color: "#2C2C2C", lineHeight: 1.2 }}
              >
                {t.accepted_title}
              </div>
              <div
                className="mt-1 italic"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#7B6A45", fontSize: "1rem" }}
              >
                {acceptedAt ? `${t.accepted_on} ${acceptedAt.toLocaleString(t.locale)}` : t.accepted_signed_fallback} · {t.accepted_thanks}
              </div>
              {(contract.document_hash || contract.signature_email) && (
                <div
                  className="mt-3 font-mono break-all"
                  style={{ fontSize: "10px", color: "#A88B5C", letterSpacing: "0.02em" }}
                >
                  {contract.signature_email && <>{t.signature_email} {contract.signature_email}<br /></>}
                  {contract.document_hash && <>{t.document_id} {contract.document_hash.slice(0, 32)}…</>}
                </div>
              )}
            </div>
          </div>
        )}
        {showQuoteOnlyAcceptedBanner && (
          <div
            className="mt-12 sm:mt-16 rounded-md p-7 sm:p-9 flex items-start gap-4"
            style={{ backgroundColor: "rgba(200,169,126,0.07)", border: "1px solid rgba(200,169,126,0.35)" }}
            data-testid="public-banner-quote-only-accepted"
          >
            <CheckCircle2 className="w-5 h-5 mt-1 shrink-0" strokeWidth={1.5} style={{ color: "#7B6A45" }} />
            <div className="flex-1">
              <div
                className="font-serif-heading italic"
                style={{ fontSize: "1.5rem", color: "#2C2C2C", lineHeight: 1.2 }}
              >
                {t.toast_quote_confirmed}
              </div>
              {acceptedAt && (
                <div
                  className="mt-1 italic"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#7B6A45", fontSize: "1rem" }}
                >
                  {t.accepted_on} {acceptedAt.toLocaleString(t.locale)}
                </div>
              )}
            </div>
          </div>
        )}
        {/* Heads-up banner: quote was confirmed earlier and now the operator
            shared the contract — signing is still required. */}
        {quoteAccepted && contractVisible && !contractSigned && (
          <div
            className="mt-12 sm:mt-16 rounded-md p-7 sm:p-9 flex items-start gap-4"
            style={{ backgroundColor: "rgba(200,169,126,0.05)", border: "1px solid rgba(200,169,126,0.4)" }}
            data-testid="public-banner-contract-pending"
          >
            <ShieldCheck className="w-5 h-5 mt-1 shrink-0" strokeWidth={1.5} style={{ color: "#C8A97E" }} />
            <div className="flex-1">
              <div
                className="font-serif-heading italic"
                style={{ fontSize: "1.4rem", color: "#2C2C2C", lineHeight: 1.2 }}
              >
                {t.esign_title}
              </div>
              <div
                className="mt-2 italic leading-relaxed"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#7B6A45", fontSize: "1rem" }}
              >
                {t.esign_desc}
              </div>
            </div>
          </div>
        )}
        {rejected && (
          <div
            className="mt-12 sm:mt-16 rounded-md p-7 sm:p-9 flex items-start gap-4"
            style={{ backgroundColor: "rgba(168,138,138,0.06)", border: "1px solid rgba(168,138,138,0.35)" }}
            data-testid="public-banner-rejected"
          >
            <XCircle className="w-5 h-5 mt-1 shrink-0" strokeWidth={1.5} style={{ color: "#A87878" }} />
            <div>
              <div
                className="font-serif-heading italic"
                style={{ fontSize: "1.4rem", color: "#5A4A4A", lineHeight: 1.2 }}
              >
                {t.rejected_title}
              </div>
              <div
                className="mt-1 italic"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#8A6A6A", fontSize: "1rem" }}
              >
                {rejectedAt ? `${t.rejected_on} ${rejectedAt.toLocaleString(t.locale)}` : t.rejected_fallback}.
                {quote.rejection_reason ? ` ${t.rejected_reason} ${quote.rejection_reason}` : ""}
              </div>
            </div>
          </div>
        )}

        {/* Preventivo — softened, borderless, italic serif descriptions so
            the proposal reads as a curated list rather than an invoice. */}
        <section
          className="rounded-md mt-12 sm:mt-16 overflow-hidden"
          style={{
            backgroundColor: "#FFFDFA",
            boxShadow: "0 1px 2px rgba(74,74,74,0.04), 0 22px 60px -32px rgba(122,98,68,0.16)",
          }}
        >
          <header className="flex items-end gap-4 px-7 sm:px-10 pt-9 pb-6">
            <div className="flex-1">
              <div
                className="label-uppercase"
                style={{ color: "#C8A97E", letterSpacing: "0.28em", fontSize: "10px" }}
              >
                {t.quote_kicker}
              </div>
              <h2
                className="font-serif-heading mt-2 leading-tight"
                style={{
                  fontSize: "clamp(1.85rem, 3vw, 2.4rem)",
                  fontStyle: "italic",
                  fontWeight: 500,
                  color: "#2C2C2C",
                  letterSpacing: "-0.005em",
                }}
              >
                {t.quote_title}
              </h2>
            </div>
            <span
              className="label-uppercase mb-1.5"
              style={{ color: "#A88B5C", letterSpacing: "0.22em", fontSize: "10px" }}
            >
              {t.quote_status[quote.status] || quote.status}
            </span>
          </header>
          <div
            className="mx-7 sm:mx-10 h-px"
            style={{ backgroundColor: "#E8DCC4", opacity: 0.6 }}
            aria-hidden="true"
          />
          <div className="px-7 sm:px-10 py-8">
            <h3
              className="label-uppercase mb-5"
              style={{ color: "#A88B5C", letterSpacing: "0.28em", fontSize: "10px" }}
              data-testid="public-included-section"
            >
              {t.included_services_section}
            </h3>
            {includedItems.length === 0 ? (
              <div className="text-sm text-neutral-500 text-center py-6">{t.no_items}</div>
            ) : (
              <div className="-mx-2 sm:mx-0">
                <table className="w-full">
                  {/* Decorative-only column headers — hidden visually so the
                      list doesn't read as a tabella. Kept in DOM (sr-only) for
                      accessibility. */}
                  <thead className="sr-only">
                    <tr>
                      <th>{t.th_description}</th>
                      <th>{t.th_qty}</th>
                      <th>{t.th_price}</th>
                      <th>{t.th_total}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {includedItems.map((it, i) => {
                      const lineSubtotal = (it.qty || 0) * (it.price || 0);
                      const lineDisc = lineSubtotal * ((it.discount || 0) / 100);
                      const lineTotal = lineSubtotal - lineDisc;
                      const isLast = i === includedItems.length - 1;
                      return (
                        <tr
                          key={i}
                          style={{
                            borderBottom: isLast ? "none" : "1px solid rgba(232,220,196,0.35)",
                          }}
                        >
                          <td className="py-7 pr-4 align-top">
                            <div
                              style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontSize: "1.35rem",
                                color: "#2C2C2C",
                                letterSpacing: "0.005em",
                                lineHeight: 1.3,
                              }}
                            >
                              {it.desc || "—"}
                            </div>
                            {it.discount > 0 && (
                              <div
                                className="mt-1 italic"
                                style={{
                                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                                  fontSize: "0.85rem",
                                  color: "#A88B5C",
                                }}
                              >
                                {t.discount_applied.replace("{p}", it.discount)}
                              </div>
                            )}
                            {it.is_optional && decisionOf(it) === "accepted" && (
                              <div
                                className="mt-1.5 italic"
                                style={{
                                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                                  fontSize: "0.8rem",
                                  color: "#7B6A45",
                                  letterSpacing: "0.04em",
                                }}
                              >
                                {t.optional_added}
                              </div>
                            )}
                          </td>
                          <td
                            className="py-7 px-3 text-right tabular-nums align-top hidden sm:table-cell"
                            style={{
                              color: "#B8A684",
                              fontSize: "0.95rem",
                              fontFamily: "'Cormorant Garamond', Georgia, serif",
                              fontStyle: "italic",
                            }}
                          >
                            {it.qty}
                          </td>
                          <td
                            className="py-7 px-3 text-right tabular-nums align-top hidden sm:table-cell"
                            style={{
                              color: "#B8A684",
                              fontSize: "0.95rem",
                              fontFamily: "'Cormorant Garamond', Georgia, serif",
                              fontStyle: "italic",
                            }}
                          >
                            {fmt(it.price)}
                          </td>
                          <td
                            className="py-7 pl-3 text-right tabular-nums align-top"
                            style={{
                              color: "#2C2C2C",
                              fontSize: "1.2rem",
                              fontFamily: "'Cormorant Garamond', Georgia, serif",
                              fontWeight: 500,
                            }}
                          >
                            {fmt(lineTotal)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {optionalItems.length > 0 && (
              <div className="mt-12" data-testid="public-optional-section">
                <div
                  className="mx-auto h-px w-10 mb-8"
                  style={{ backgroundColor: "#E8DCC4", opacity: 0.7 }}
                  aria-hidden="true"
                />
                <h3
                  className="font-serif-heading"
                  style={{
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                    fontSize: "1.75rem",
                    color: "#2C2C2C",
                    letterSpacing: "0.005em",
                    lineHeight: 1.2,
                  }}
                >
                  {t.optional_services_section}
                </h3>
                <p
                  className="mt-2"
                  style={{
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                    color: "#5C4D32",
                    fontSize: "1.02rem",
                    lineHeight: 1.55,
                    maxWidth: "52ch",
                  }}
                >
                  {t.optional_subtitle}
                </p>
                {/* Instructional micro-block — placed RIGHT ABOVE the cards
                    so the visitor reads "tick the box" the moment their eyes
                    move down. Champagne tone, no warning vibe. Hidden once
                    the decision has been taken (becomes redundant). */}
                {!decisionTaken && (
                  <div
                    className="mt-5 rounded-md px-4 py-3"
                    style={{ backgroundColor: "#FAF3E7", border: "1px solid #E5D2A8" }}
                    data-testid="public-optional-instructions"
                  >
                    <p style={{ color: "#5C4D32", fontSize: "0.92rem", lineHeight: 1.4 }}>
                      {t.optional_instructions}
                    </p>
                    <p className="mt-1" style={{ color: "#9A8861", fontSize: "0.78rem", lineHeight: 1.4 }}>
                      {t.optional_window_note}
                    </p>
                  </div>
                )}
                {decisionTaken && (
                  <p className="text-xs mt-4 italic" style={{ color: "#9A8861" }}>
                    {t.optional_locked}
                  </p>
                )}
                <div className="mt-5 space-y-3">
                  {optionalItems.map(({ it, idx }) => {
                    const qtyN = parseFloat(it.qty) || 0;
                    const priceN = parseFloat(it.price) || 0;
                    const discN = parseFloat(it.discount) || 0;
                    const lineTotal = qtyN * priceN * (1 - discN / 100);
                    const decision = decisionOf(it);
                    const isAccepted = decision === "accepted";
                    const isDeclined = decision === "declined";
                    const isPending = decision === "pending";
                    const justAdded = (justAddedOpt === idx) && isAccepted;
                    // Tone palette — luxury minimal:
                    //   pending  → cream neutral, hairline border (does not scream)
                    //   accepted → champagne soft fill + gold border + subtle ring
                    //   declined → almost-disabled grey, readonly look (no error red)
                    const wrapperStyle = isAccepted
                      ? { backgroundColor: "#FAF3E7", borderColor: "#E5D2A8", boxShadow: "0 0 0 1px rgba(200,169,126,0.15)" }
                      : isDeclined
                        ? { backgroundColor: "#FAFAF8", borderColor: "#E5E1DA", opacity: 0.7 }
                        : { backgroundColor: "#FFFFFF", borderColor: "#EAE7E0" };
                    const badgeClass = isAccepted
                      ? "bg-[#FAF3E7] text-[#7A5C2E] border border-[#E5D2A8]"
                      : isDeclined
                        ? "bg-neutral-100 text-neutral-500 border border-neutral-200"
                        : "bg-white text-[#9A8861] border border-[#EAE7E0]";
                    const badgeText = isAccepted
                      ? t.optional_added
                      : isDeclined
                        ? t.optional_not_added
                        : (t.optional_pending || t.optional_not_added);
                    const checkboxLocked = decisionTaken || optBusy;
                    // Clicking anywhere on the card toggles the selection,
                    // EXCEPT when the click originated inside an interactive
                    // child (qty input, the checkbox itself). This makes the
                    // touch/iPad target much larger without breaking inputs.
                    const onCardClick = (e) => {
                      if (checkboxLocked) return;
                      const tag = (e.target?.tagName || "").toLowerCase();
                      if (tag === "input" || tag === "label" || tag === "button") return;
                      // Ignore clicks inside any element marked as data-stop-toggle
                      if (e.target?.closest?.("[data-stop-toggle]")) return;
                      toggleOptionalSelected(idx, !isAccepted);
                    };
                    return (
                      <div
                        key={idx}
                        role={checkboxLocked ? undefined : "button"}
                        tabIndex={checkboxLocked ? -1 : 0}
                        onClick={onCardClick}
                        onKeyDown={(e) => {
                          if (checkboxLocked) return;
                          if (e.key === " " || e.key === "Enter") {
                            e.preventDefault();
                            toggleOptionalSelected(idx, !isAccepted);
                          }
                        }}
                        className={`opt-card rounded-md border p-4 sm:p-5 transition-all duration-200 ${checkboxLocked ? "" : "cursor-pointer hover:-translate-y-[1px]"} ${justAdded ? "opt-card-just-added" : ""}`}
                        style={wrapperStyle}
                        data-testid={`public-optional-row-${idx}`}
                        data-decision={decision}
                      >
                        <div className="flex flex-wrap items-center gap-4">
                          {/* Luxury square checkbox — replaces the previous
                              Heart icon. Visible affordance: the visitor
                              recognizes it instantly as "select this". */}
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked={isAccepted}
                            disabled={checkboxLocked}
                            data-stop-toggle
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!checkboxLocked) toggleOptionalSelected(idx, !isAccepted);
                            }}
                            aria-label={isAccepted ? t.optional_added : t.optional_pending}
                            className={`opt-check ${isAccepted ? "is-on" : ""}`}
                            data-testid={`public-optional-checkbox-${idx}`}
                          >
                            {isAccepted && (
                              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <path d="M4 12.5l5 5 11-12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </button>
                          <div className="flex-1 min-w-0">
                            <div
                              className={isDeclined ? "line-through" : ""}
                              style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontSize: "1.15rem",
                                color: isDeclined ? "#A8A29E" : "#2C2C2C",
                                letterSpacing: "0.005em",
                                lineHeight: 1.3,
                              }}
                            >
                              {it.desc || "—"}
                            </div>
                            <div
                              className="mt-1 italic"
                              style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                color: "#A88B5C",
                                fontSize: "0.92rem",
                              }}
                            >
                              {fmt(it.price)} × {qtyN}
                              {discN > 0 ? ` · ${t.discount_applied.replace("{p}", discN)}` : ""}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-stop-toggle>
                            <Label
                              htmlFor={`public-opt-qty-${idx}`}
                              className="text-[11px] uppercase tracking-wide text-neutral-500"
                            >
                              {t.optional_quantity}
                            </Label>
                            <Input
                              id={`public-opt-qty-${idx}`}
                              type="number"
                              min="0"
                              max="1000"
                              step="1"
                              disabled={decisionTaken || optBusy}
                              value={qtyN}
                              onChange={(e) => updateOptionalQty(idx, e.target.value)}
                              onBlur={() => pushOptionalChange(items)}
                              onClick={(e) => e.stopPropagation()}
                              className="h-9 w-20 rounded-sm text-right"
                              data-testid={`public-optional-qty-${idx}`}
                            />
                          </div>
                          <div className={`text-right tabular-nums min-w-[6ch] ${isAccepted ? "font-medium" : "text-neutral-400 line-through"}`}>
                            {fmt(lineTotal)}
                          </div>
                        </div>
                        <div className="mt-2 text-[11px] flex items-center gap-2 flex-wrap">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-sm uppercase tracking-wide ${badgeClass}`}
                            data-testid={`public-optional-badge-${idx}`}
                          >
                            {badgeText}
                          </span>
                          {/* Soft micro-feedback shown right after the user
                              flips the heart on (auto-fades after 2.4s via the
                              justAddedOpt state reset). */}
                          {justAdded && (
                            <span
                              className="opt-just-added text-[11px]"
                              style={{ color: "#7B6A45" }}
                              data-testid={`public-optional-just-added-${idx}`}
                            >
                              {t.optional_just_added}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-14 ml-auto w-full sm:max-w-md">
              {/* Quiet pre-total ledger — subtotal · discount · VAT */}
              <div className="space-y-2.5 pb-7">
                <Row label={t.subtotal} value={fmt(liveSubtotal)} />
                {quote.discount_total > 0 && (
                  <Row label={`${t.general_discount} (${quote.discount_total}%)`} value={`- ${fmt(liveSubtotal * (quote.discount_total / 100))}`} />
                )}
                {quote.tax > 0 && (
                  <Row label={`${t.vat} (${quote.tax}%)`} value={fmt(liveTotal - liveAfterDisc)} />
                )}
              </div>
              {/* The total — emotional centrepiece, framed by two thin gold
                  rules. Big italic serif, gold whisper underneath. */}
              <div
                className="text-center py-9"
                style={{
                  borderTop: "1px solid rgba(200,169,126,0.55)",
                  borderBottom: "1px solid rgba(200,169,126,0.55)",
                }}
              >
                <div
                  className="label-uppercase"
                  style={{ color: "#A88B5C", letterSpacing: "0.32em", fontSize: "10px" }}
                >
                  {t.total_row}
                </div>
                <div
                  className="mt-3 tabular-nums"
                  style={{
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                    fontStyle: "italic",
                    fontWeight: 500,
                    color: "#2C2C2C",
                    fontSize: "clamp(2.6rem, 5vw, 3.6rem)",
                    lineHeight: 1,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {fmt(liveTotal)}
                </div>
              </div>
              {/* Quiet post-total ledger — acconto + saldo. When multiple
                  deposits have been registered we break them down line-by-line
                  (with date + notes in italic muted) so the bride sees the
                  full payment history above the SUM row. */}
              <div className="space-y-2.5 pt-7" data-testid="public-quote-ledger">
                {Array.isArray(quote.deposits) && quote.deposits.length > 1 && (
                  <div className="space-y-1.5 pb-1" data-testid="public-quote-deposits-breakdown">
                    {quote.deposits.map((d, i) => {
                      const parts = [];
                      if (d?.date) parts.push(d.date);
                      if (d?.notes) parts.push(d.notes);
                      const subLabel = parts.length
                        ? `${t.deposit} · ${parts.join(" · ")}`
                        : t.deposit;
                      return (
                        <Row
                          key={i}
                          label={subLabel}
                          value={fmt(parseFloat(d?.amount) || 0)}
                          muted
                        />
                      );
                    })}
                  </div>
                )}
                <Row label={t.deposit} value={fmt(liveDeposit)} />
                <Row label={t.balance} value={fmt(liveBalance)} muted />
              </div>
              {(quote.tax_mode === "forfettario" || Number(quote.tax) === 0) && (
                <p
                  className="italic text-center mt-7 leading-snug"
                  style={{
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                    color: "#A88B5C",
                    fontSize: "0.82rem",
                  }}
                  data-testid="public-forfettario-notice"
                >
                  Operazione in franchigia da IVA ai sensi dell'art. 1, commi 54-89, L. 190/2014
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Contratto — visible only when the operator has shared it */}
        {contractVisible && (
          <section
            className="rounded-md mt-16 sm:mt-20 overflow-hidden"
            style={{
              backgroundColor: "#FFFDFA",
              boxShadow: "0 1px 2px rgba(74,74,74,0.04), 0 22px 60px -32px rgba(122,98,68,0.16)",
            }}
          >
            <header className="px-7 sm:px-10 pt-9 pb-6">
              <div
                className="label-uppercase"
                style={{ color: "#C8A97E", letterSpacing: "0.28em", fontSize: "10px" }}
              >
                {t.contract_kicker}
              </div>
              <h2
                className="font-serif-heading mt-2 leading-tight"
                style={{
                  fontSize: "clamp(1.85rem, 3vw, 2.4rem)",
                  fontStyle: "italic",
                  fontWeight: 500,
                  color: "#2C2C2C",
                  letterSpacing: "-0.005em",
                }}
              >
                {t.contract_title}
              </h2>
            </header>
            <div
              className="mx-7 sm:mx-10 h-px"
              style={{ backgroundColor: "#E8DCC4", opacity: 0.6 }}
              aria-hidden="true"
            />
            <div className="px-7 sm:px-10 py-9 space-y-9">
              <div>
                <h3
                  className="label-uppercase mb-3"
                  style={{ color: "#A88B5C", letterSpacing: "0.28em", fontSize: "10px" }}
                >
                  {t.conditions_section}
                </h3>
                <pre
                  className="whitespace-pre-wrap leading-relaxed"
                  style={{
                    fontFamily: "'Outfit', system-ui, sans-serif",
                    fontSize: "0.95rem",
                    color: "#5A5A5A",
                    lineHeight: 1.75,
                  }}
                >
                  {contract.conditions}
                </pre>
              </div>
              <div>
                <h3
                  className="label-uppercase mb-3"
                  style={{ color: "#A88B5C", letterSpacing: "0.28em", fontSize: "10px" }}
                >
                  {t.payment_section}
                </h3>
                <pre
                  className="whitespace-pre-wrap leading-relaxed"
                  style={{
                    fontFamily: "'Outfit', system-ui, sans-serif",
                    fontSize: "0.95rem",
                    color: "#5A5A5A",
                    lineHeight: 1.75,
                  }}
                >
                  {contract.payment_terms}
                </pre>
              </div>
              <p
                className="italic pt-6"
                style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  color: "#A88B5C",
                  fontSize: "0.95rem",
                  borderTop: "1px solid rgba(232,220,196,0.6)",
                  lineHeight: 1.6,
                }}
              >
                {t.acceptance_note}
              </p>
            </div>
          </section>
        )}

        {/* Note del preventivo — quick notes (resolved) + manual notes.
            Identical text to what the PDF renders, always visible without download. */}
        {((quote.quick_notes_text && quote.quick_notes_text.length > 0) || (quote.notes && quote.notes.trim().length > 0)) && (
          <section
            className="rounded-md mt-16 sm:mt-20 overflow-hidden"
            style={{
              backgroundColor: "#FFFDFA",
              boxShadow: "0 1px 2px rgba(74,74,74,0.04), 0 22px 60px -32px rgba(122,98,68,0.16)",
            }}
            data-testid="public-quote-notes-section"
          >
            <header className="px-7 sm:px-10 pt-9 pb-6">
              <div
                className="label-uppercase"
                style={{ color: "#C8A97E", letterSpacing: "0.28em", fontSize: "10px" }}
              >
                {t.notes_section_subtitle}
              </div>
              <h2
                className="font-serif-heading mt-2 leading-tight"
                style={{
                  fontSize: "clamp(1.85rem, 3vw, 2.4rem)",
                  fontStyle: "italic",
                  fontWeight: 500,
                  color: "#2C2C2C",
                }}
              >
                {t.notes_section_title}
              </h2>
            </header>
            <div
              className="mx-7 sm:mx-10 h-px"
              style={{ backgroundColor: "#E8DCC4", opacity: 0.6 }}
              aria-hidden="true"
            />
            <div className="px-7 sm:px-10 py-9">
              <ul className="space-y-5 list-none pl-0">
                {(quote.quick_notes_text || []).map((txt, i) => (
                  <li
                    key={`qn-${i}`}
                    className="leading-relaxed flex gap-4"
                    style={{
                      fontFamily: "'Cormorant Garamond', Georgia, serif",
                      fontSize: "1.1rem",
                      color: "#4A4A4A",
                      lineHeight: 1.65,
                    }}
                    data-testid={`public-quick-note-${i}`}
                  >
                    <span
                      style={{ color: "#C8A97E", flex: "none", marginTop: "2px" }}
                      aria-hidden="true"
                    >
                      ✦
                    </span>
                    <span>{txt}</span>
                  </li>
                ))}
                {quote.notes && quote.notes.trim() && (
                  <li
                    className="leading-relaxed whitespace-pre-line flex gap-4"
                    style={{
                      fontFamily: "'Cormorant Garamond', Georgia, serif",
                      fontSize: "1.1rem",
                      color: "#4A4A4A",
                      lineHeight: 1.65,
                    }}
                    data-testid="public-manual-notes"
                  >
                    <span
                      style={{ color: "#C8A97E", flex: "none", marginTop: "2px" }}
                      aria-hidden="true"
                    >
                      ✦
                    </span>
                    <span>{quote.notes}</span>
                  </li>
                )}
              </ul>
            </div>
          </section>
        )}

        {/* CTA — OTP signing flow.
            Shown whenever the contract is visible and not yet signed/rejected.
            CRITICAL: also shown when the quote was already accepted via the
            quote-only flow and the operator later shared the contract — the
            signature with OTP is mandatory before contract.status="firmato". */}
        {!signFlowFinished && contractVisible && (
          <div
            className="rounded-md mt-20 sm:mt-24 p-10 sm:p-14 text-center"
            style={{
              backgroundColor: "#FFFDFA",
              boxShadow: "0 1px 2px rgba(74,74,74,0.04), 0 30px 80px -32px rgba(122,98,68,0.22)",
            }}
            data-testid="public-decision-cta"
          >
            <div
              className="label-uppercase"
              style={{ color: "#C8A97E", letterSpacing: "0.32em", fontSize: "10px" }}
            >
              {t.esign_title}
            </div>
            <h2
              className="font-serif-heading mt-4 mx-auto"
              style={{
                fontSize: "clamp(1.85rem, 3.6vw, 2.8rem)",
                fontStyle: "italic",
                fontWeight: 500,
                color: "#2C2C2C",
                lineHeight: 1.15,
                maxWidth: "22ch",
                letterSpacing: "-0.005em",
              }}
            >
              {brideName ? `${brideName}, è il momento di renderlo Suo` : t.esign_title}
            </h2>
            <div
              className="mx-auto mt-6 h-px w-12"
              style={{ backgroundColor: "#C8A97E", opacity: 0.55 }}
              aria-hidden="true"
            />
            <p
              className="mx-auto mt-6 leading-relaxed"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontStyle: "italic",
                color: "#7B6A45",
                fontSize: "clamp(1rem, 1.5vw, 1.15rem)",
                maxWidth: "52ch",
              }}
            >
              {t.esign_desc}
            </p>

            {signStep === "idle" && (
              <>
                <label
                  className={`mx-auto flex items-start gap-3 mt-9 max-w-xl text-left p-5 rounded-md cursor-pointer transition-colors`}
                  style={{
                    backgroundColor: consent ? "rgba(200,169,126,0.08)" : "rgba(248,245,241,0.5)",
                    border: consent ? "1px solid rgba(200,169,126,0.5)" : "1px solid rgba(232,220,196,0.7)",
                  }}
                  data-testid="consent-row"
                >
                  <Checkbox
                    checked={consent}
                    onCheckedChange={(v) => setConsent(!!v)}
                    className="mt-0.5 data-[state=checked]:bg-[#C8A97E] data-[state=checked]:border-[#C8A97E]"
                    data-testid="consent-checkbox"
                  />
                  <span
                    style={{
                      fontFamily: "'Cormorant Garamond', Georgia, serif",
                      fontSize: "1.05rem",
                      color: "#4A4A4A",
                      lineHeight: 1.55,
                    }}
                  >
                    {t.esign_consent}
                  </span>
                </label>

                <div className="flex flex-wrap justify-center gap-4 mt-8">
                  <Button
                    onClick={requestOtp}
                    disabled={!consent || submitting}
                    className="text-white text-[14px] tracking-[0.18em] uppercase transition-all disabled:opacity-50"
                    style={{
                      backgroundColor: (!consent || submitting) ? "#D9C9B2" : "#C8A97E",
                      borderRadius: "999px",
                      boxShadow: "0 8px 24px -8px rgba(200,169,126,0.55)",
                      height: "56px",
                      padding: "0 36px",
                      fontWeight: 500,
                    }}
                    onMouseEnter={(e) => { if (consent && !submitting) e.currentTarget.style.backgroundColor = "#B8956A"; }}
                    onMouseLeave={(e) => { if (consent && !submitting) e.currentTarget.style.backgroundColor = "#C8A97E"; }}
                    data-testid="public-accept-button"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} /> : null}
                    {t.accept_sign}
                  </Button>
                  <Button
                    onClick={() => setRejectOpen(true)}
                    disabled={submitting}
                    variant="ghost"
                    className="rounded-full text-[12px] tracking-[0.18em] uppercase"
                    style={{
                      height: "56px",
                      padding: "0 24px",
                      color: "#A8A29E",
                      fontWeight: 400,
                    }}
                    data-testid="public-reject-button"
                  >
                    {t.reject}
                  </Button>
                </div>
              </>
            )}

            {signStep === "otp_sent" && (
              <div className="mt-5 space-y-4">
                <div className="bg-secondary/30 border border-border rounded-sm p-4 text-sm" data-testid="otp-info">
                  <div className="font-medium">{t.otp_sent_title}</div>
                  {otpSentTo && <div className="text-xs text-neutral-500 mt-0.5">{t.otp_sent_to} {otpSentTo}</div>}
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {t.otp_expiry_note}
                  </div>
                </div>
                {demoOtp && (
                  <div className="bg-amber-50 border border-amber-300 rounded-sm p-4 flex items-center gap-3" data-testid="demo-otp-banner">
                    <span className="label-uppercase text-amber-900 px-2 py-0.5 rounded-sm bg-amber-200 text-[10px]">{t.demo_label}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-amber-900">{t.demo_note}</div>
                      <div className="font-mono text-2xl tracking-widest text-amber-900 mt-1" data-testid="demo-otp-code">{demoOtp}</div>
                    </div>
                    <Button variant="outline" size="sm" className="rounded-sm" onClick={copyDemoOtp}>
                      <Copy className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> {t.copy}
                    </Button>
                  </div>
                )}

                <div>
                  <Label className="label-uppercase">{t.otp_input_label}</Label>
                  <Input
                    inputMode="numeric"
                    maxLength={6}
                    pattern="\d{6}"
                    value={otpCode}
                    onChange={(e) => { setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setOtpError(""); }}
                    placeholder="______"
                    className="mt-1 rounded-sm h-14 text-center font-mono text-3xl tracking-[0.5em]"
                    data-testid="otp-input"
                    autoFocus
                  />
                  {otpError && <div className="text-xs text-red-600 mt-1.5" data-testid="otp-error">{otpError}</div>}
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={verifyOtp}
                    disabled={submitting || otpCode.length !== 6}
                    className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-6 text-base"
                    data-testid="otp-verify-button"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} /> : <ShieldCheck className="w-4 h-4 mr-2" strokeWidth={1.5} />}
                    {t.confirm_signature}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={resendOtp}
                    disabled={submitting}
                    className="rounded-sm h-12 px-6"
                    data-testid="otp-resend-button"
                  >
                    <RotateCw className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t.resend_code}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => { setSignStep("idle"); setOtpCode(""); setOtpError(""); setDemoOtp(""); }}
                    disabled={submitting}
                    className="rounded-sm h-12 px-4 text-neutral-500"
                  >
                    {t.cancel}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quote-only confirmation CTA — shown when the contract has NOT been
            shared with the client yet. There is NO signature step here; the
            cliente simply confirms the proposal and the operator will issue the
            contract for signature later. */}
        {!signFlowFinished && !decisionTaken && !contractVisible && (
          <div
            className="rounded-md mt-20 sm:mt-24 p-10 sm:p-14 text-center"
            style={{
              backgroundColor: "#FFFDFA",
              boxShadow: "0 1px 2px rgba(74,74,74,0.04), 0 30px 80px -32px rgba(122,98,68,0.22)",
            }}
            data-testid="public-decision-cta-quote-only"
          >
            <div
              className="label-uppercase"
              style={{ color: "#C8A97E", letterSpacing: "0.32em", fontSize: "10px" }}
            >
              {t.confirm_quote_title}
            </div>
            <h2
              className="font-serif-heading mt-4 mx-auto"
              style={{
                fontSize: "clamp(1.85rem, 3.6vw, 2.8rem)",
                fontStyle: "italic",
                fontWeight: 500,
                color: "#2C2C2C",
                lineHeight: 1.15,
                maxWidth: "22ch",
                letterSpacing: "-0.005em",
              }}
            >
              {brideName ? `${brideName}, lasci che ce ne occupiamo noi` : t.confirm_quote_title}
            </h2>
            <div
              className="mx-auto mt-6 h-px w-12"
              style={{ backgroundColor: "#C8A97E", opacity: 0.55 }}
              aria-hidden="true"
            />
            <p
              className="mx-auto mt-6 leading-relaxed"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontStyle: "italic",
                color: "#7B6A45",
                fontSize: "clamp(1rem, 1.5vw, 1.15rem)",
                maxWidth: "52ch",
              }}
            >
              {t.confirm_quote_desc}
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-9">
              <Button
                onClick={confirmQuoteOnly}
                disabled={submitting}
                className="text-white text-[14px] tracking-[0.18em] uppercase transition-all"
                style={{
                  backgroundColor: "#C8A97E",
                  borderRadius: "999px",
                  boxShadow: "0 8px 24px -8px rgba(200,169,126,0.55)",
                  height: "56px",
                  padding: "0 36px",
                  fontWeight: 500,
                }}
                onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.backgroundColor = "#B8956A"; }}
                onMouseLeave={(e) => { if (!submitting) e.currentTarget.style.backgroundColor = "#C8A97E"; }}
                data-testid="public-confirm-quote-button"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} /> : null}
                {t.confirm_quote_button}
              </Button>
              <Button
                onClick={() => setRejectOpen(true)}
                disabled={submitting}
                variant="ghost"
                className="rounded-full text-[12px] tracking-[0.18em] uppercase"
                style={{
                  height: "56px",
                  padding: "0 24px",
                  color: "#A8A29E",
                  fontWeight: 400,
                }}
                data-testid="public-reject-button-quote-only"
              >
                {t.reject}
              </Button>
            </div>
            {/* Reassuring subtext under the CTA — sets expectation about
                the 24h reservation window and the deferred contract step. */}
            <p
              className="mt-7 leading-relaxed mx-auto italic"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                color: "#A88B5C",
                fontSize: "0.95rem",
                maxWidth: "52ch",
              }}
              data-testid="public-confirm-quote-subtext"
            >
              {t.confirm_quote_subtext}
            </p>
          </div>
        )}

        <div
          className="text-center mt-16 mb-8"
          data-testid="public-client-pdf-bottom-wrapper"
        >
          <a
            href={`${API}/public/client/${token}/pdf${langOverride ? `?lang=${langOverride}` : ""}`}
            target="_blank"
            rel="noreferrer"
            data-testid="public-client-pdf"
            className="inline-flex items-center gap-2 italic transition-colors"
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              color: "#A88B5C",
              fontSize: "1.05rem",
              textDecoration: "underline",
              textDecorationColor: "rgba(200,169,126,0.4)",
              textUnderlineOffset: "6px",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "#7B6A45"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "#A88B5C"; }}
          >
            <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
            {t.download_pdf}
          </a>
        </div>

        <div
          className="text-center mt-10"
          style={{ color: "#B8AC9A", fontSize: "10px", letterSpacing: "0.24em" }}
        >
          {t.powered_by} <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic", color: "#A88B5C", letterSpacing: "0.04em" }}>AI Bridal Manager</span>™
        </div>
      </div>

      {/* Reject reason dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="public-reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-heading text-2xl">{t.reject_dialog_title}</DialogTitle>
            <DialogDescription>
              {t.reject_dialog_desc}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="label-uppercase">{t.reject_reason_label}</Label>
            <Textarea
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t.reject_placeholder}
              className="mt-1 rounded-sm"
              data-testid="public-reject-reason"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setRejectOpen(false)} disabled={submitting}>
              {t.cancel}
            </Button>
            <Button
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
              onClick={reject}
              disabled={submitting}
              data-testid="public-reject-confirm"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} /> : null}
              {t.confirm_reject}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, bold, muted }) {
  return (
    <div className="flex justify-between items-baseline gap-6">
      <span
        className="uppercase"
        style={{
          fontFamily: "'Outfit', system-ui, sans-serif",
          letterSpacing: "0.18em",
          fontSize: "10px",
          color: muted ? "#B8AC9A" : "#A88B5C",
        }}
      >
        {label}
      </span>
      <span
        className="tabular-nums"
        style={
          bold
            ? {
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontStyle: "italic",
                fontSize: "1.85rem",
                color: "#2C2C2C",
                lineHeight: 1.1,
              }
            : muted
              ? {
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontSize: "1rem",
                  color: "#8A8278",
                }
              : {
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontSize: "1.05rem",
                  color: "#4A4A4A",
                }
        }
      >
        {value}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* LANGUAGE SWITCHER — discreet floating pill, fixed top-right.        */
/* ------------------------------------------------------------------ */
/**
 * Tiny floating control that lets the bride flip the entire public page
 * between the 4 supported languages. Persisted via `?lang=` URL param so
 * refreshes / shared links keep the choice. Highlights the current lang.
 *
 * Design rationale:
 *  - Fixed position so it stays in reach while the bride scrolls the
 *    long brochure; doesn't fight with the brand cover or hero section.
 *  - Champagne-gold accents to match the brochure's editorial palette.
 *  - Backdrop blur + soft shadow keeps it readable over any background
 *    (white hero, dark cover, beige brochure).
 *  - When `loading` is true we dim + disable to prevent rapid clicking
 *    from queueing multiple in-flight refreshes.
 */
function LanguageSwitcher({ current, onChange, loading }) {
  const LANGS = [
    { code: "it", label: "IT" },
    { code: "en", label: "EN" },
    { code: "es", label: "ES" },
    { code: "fr", label: "FR" },
  ];
  return (
    <div
      className="fixed top-4 right-4 sm:top-6 sm:right-6 z-50 pc-fade-in"
      data-testid="public-language-switcher"
    >
      <div
        className="inline-flex items-center gap-0.5 rounded-full bg-white/85 backdrop-blur-md border border-[#E6D9C6] shadow-[0_4px_16px_rgba(168,139,92,0.12)] p-1"
        role="tablist"
        aria-label="Lingua / Language"
      >
        {LANGS.map((l) => {
          const active = (current || "it").toLowerCase() === l.code;
          return (
            <button
              key={l.code}
              type="button"
              role="tab"
              aria-selected={active}
              disabled={loading || active}
              onClick={() => onChange(l.code)}
              className="text-[11px] font-medium tracking-[0.12em] uppercase rounded-full transition-all duration-200 disabled:cursor-default"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                padding: "5px 11px",
                background: active ? "#A88B5C" : "transparent",
                color: active ? "#FFFFFF" : (loading ? "#C9BFB0" : "#7B6A45"),
                opacity: loading && !active ? 0.6 : 1,
              }}
              data-testid={`public-language-switcher-${l.code}`}
            >
              {l.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

